import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api, type Announcement } from "../api.js";
import { SERVICE_IDS, LANGS } from "../constants.js";

interface Props {
  mode: "create" | "edit";
}

export function AnnouncementForm({ mode }: Props) {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEdit = mode === "edit";

  const [serviceId, setServiceId] = useState("global");
  const [lang, setLang] = useState("ja");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [tagsText, setTagsText] = useState("");
  const [translateTo, setTranslateTo] = useState<string[]>([]);
  const [translations, setTranslations] = useState<Announcement[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!isEdit || !id) return;
    void (async () => {
      try {
        const r = await api.getAnnouncement(id);
        setServiceId(r.item.service_id);
        setLang(r.item.lang);
        setTitle(r.item.title);
        setBody(r.item.body);
        setImageUrl(r.item.image_url ?? "");
        setTagsText(r.item.tags.join(","));
        setTranslations(r.translations);
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, [isEdit, id]);

  const langOptions = useMemo(() => LANGS.filter((l) => l !== lang), [lang]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const tags = tagsText.split(",").map((s) => s.trim()).filter(Boolean);
      if (isEdit && id) {
        await api.updateAnnouncement(id, {
          title,
          body,
          image_url: imageUrl || null,
          tags,
        });
      } else {
        await api.createAnnouncement({
          service_id: serviceId,
          lang,
          title,
          body,
          image_url: imageUrl || undefined,
          tags,
          translate_to: translateTo,
        });
      }
      navigate("/announcements");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function onAddTranslation() {
    if (!id) return;
    setError(null);
    try {
      const langs = translateTo.filter((l) => l !== lang);
      if (langs.length === 0) {
        setError("追加する言語を選んでください");
        return;
      }
      const r = await api.translateAnnouncement(id, { target_langs: langs });
      setTranslations(r.translations);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function onHide() {
    if (!id) return;
    if (!confirm("このお知らせを非表示にしますか？ (ends_at を現在時刻に設定)")) return;
    try {
      await api.updateAnnouncement(id, { ends_at: new Date().toISOString() });
      navigate("/announcements");
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function onImageFile(file: File) {
    setUploading(true);
    setError(null);
    try {
      const safeName = file.name.replace(/[^A-Za-z0-9._-]/g, "_");
      const presigned = await api.presignImage({ filename: safeName, content_type: file.type as "image/png" });
      const put = await fetch(presigned.upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!put.ok) throw new Error(`画像アップ失敗: ${put.status}`);
      setImageUrl(presigned.public_url);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUploading(false);
    }
  }

  return (
    <section data-testid="announcement-form">
      <h1>{isEdit ? "お知らせを編集" : "お知らせを新規投稿"}</h1>
      {error && <div className="error-banner" data-testid="form-error">{error}</div>}
      <form className="row" onSubmit={onSubmit}>
        <div className="split">
          <label className="field">
            <span>service_id</span>
            <select value={serviceId} onChange={(e) => setServiceId(e.target.value)} disabled={isEdit}>
              {SERVICE_IDS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="field">
            <span>lang</span>
            <select value={lang} onChange={(e) => setLang(e.target.value)} disabled={isEdit}>
              {LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
        </div>
        <label className="field">
          <span>タイトル</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            data-testid="title-input"
          />
        </label>
        <label className="field">
          <span>本文 (Markdown)</span>
          <div className="split">
            <textarea value={body} onChange={(e) => setBody(e.target.value)} required data-testid="body-input" />
            <div className="preview-pane" data-testid="markdown-preview">
              <ReactMarkdown>{body || "*プレビューはここに表示されます*"}</ReactMarkdown>
            </div>
          </div>
        </label>
        <label className="field">
          <span>tags (カンマ区切り)</span>
          <input type="text" value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="info,maintenance" />
        </label>
        <label className="field">
          <span>image_url</span>
          <input type="text" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} />
        </label>
        <label className="field">
          <span>画像アップロード</span>
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onImageFile(f);
            }}
          />
          {uploading && <span>アップロード中…</span>}
        </label>
        <div className="field">
          <span>翻訳先言語 {isEdit ? "(後追い翻訳)" : "(同時投稿)"}</span>
          <div className="lang-checkboxes" data-testid="translate-langs">
            {langOptions.map((l) => (
              <label key={l}>
                <input
                  type="checkbox"
                  checked={translateTo.includes(l)}
                  onChange={(e) =>
                    setTranslateTo((prev) =>
                      e.target.checked ? [...prev, l] : prev.filter((x) => x !== l),
                    )
                  }
                />
                {l}
              </label>
            ))}
          </div>
        </div>
        <div className="toolbar">
          <button type="submit" className="btn" disabled={submitting} data-testid="submit-btn">
            {submitting ? "保存中…" : isEdit ? "更新する" : "投稿する"}
          </button>
          {isEdit && (
            <>
              <button type="button" className="btn secondary" onClick={onAddTranslation}>
                選択言語に翻訳を追加
              </button>
              <button type="button" className="btn danger" onClick={onHide}>
                今すぐ非表示にする
              </button>
            </>
          )}
        </div>
      </form>
      {isEdit && translations.length > 0 && (
        <section style={{ marginTop: 24 }}>
          <h2>翻訳バージョン</h2>
          <table className="list">
            <thead>
              <tr><th>lang</th><th>title</th></tr>
            </thead>
            <tbody>
              {translations.map((t) => (
                <tr key={t.id}><td>{t.lang}</td><td>{t.title}</td></tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </section>
  );
}
